import { Box, Chip, Divider, MenuItem, Paper, TextField, Typography } from '@mui/material';
import { NumericTextField } from '../../../../components/forms/NumericTextField';
import { FieldEvidenceButton } from '../../../../components/evidence/FieldEvidenceButton';
import type { RockBTSSpecimen } from '../../../../types/rockBts';
import type { BtsSpecimenPreview } from '../computations';
import {
  BTS_FAILURE_TYPE_OPTIONS,
  BTS_FAILURE_TYPES_WITH_SECONDARY,
  BTS_FRACTURE_MODE_OPTIONS,
  BTS_NOTE_1,
  BTS_NOTE_2,
  BTS_PLANE_TYPE_OPTIONS,
} from '../rockBtsFields';
// Shared section idiom (reused read-only from the Rock TCS module)
import { DerivedValue, fmt, inputSx, paperSx, SectionHeader, unit } from '../../rock-tcs/sectionStyles';
import { useTestFormulas } from '../../../../hooks/useTestFormulas';
import { ReferenceFigureButton } from './ReferenceFigureButton';
import deviationFigure from '../../../../assets/rock-bts-deviation-x.png';
import axisAnglesFigure from '../../../../assets/rock-bts-axis-angles.png';
// BUG-2026-0133 — figures relocated inline from the old right-hand ReferencePanel:
// fracture-type examples sit beside "Failure Type & Measurements" (green) and the
// failure-description examples beside "Failure (Primary)" (blue), both as click-to-
// open popups via ReferenceFigureButton.
import fractureTypesFigure from '../../../../assets/rock-bts-fracture-types.png';
import failureExamplesFigure from '../../../../assets/rock-bts-failure-examples.png';

interface Props {
  specimen: RockBTSSpecimen;
  preview: BtsSpecimenPreview;
  title: string;
  onChange: (field: keyof RockBTSSpecimen, value: RockBTSSpecimen[keyof RockBTSSpecimen]) => void;
  readOnly?: boolean;
}

/**
 * Result Entry — one specimen's card (mockup: Rock BTS Result Entry.png):
 * Loading information (REQ-05/06/07; flat platens hard-coded), Failure Type +
 * X-deviation helper (REQ-09), primary/secondary failure rows + auto
 * failure-description preview with override (REQ-08).
 */
export const SpecimenResultCard = ({ specimen, preview, title, onChange, readOnly }: Props) => {
  const num = (field: keyof RockBTSSpecimen) => (value: number | null) => onChange(field, value);
  // Formula-registry tooltips for the computed tiles (FEAT "Formula Registry").
  const { formulaFor } = useTestFormulas('rockBts');
  const hasSecondary = specimen.failureType
    ? BTS_FAILURE_TYPES_WITH_SECONDARY.has(specimen.failureType)
    : false;

  const failureRow = (prefix: 'primary' | 'secondary', disabled: boolean) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1.25 }}>
      <TextField
        select size="small" label={`${prefix === 'primary' ? 'Fracture Mode' : 'Fracture Mode (secondary)'}`}
        value={(specimen[`${prefix}FractureMode` as keyof RockBTSSpecimen] as string | null) ?? ''}
        onChange={(e) => onChange(`${prefix}FractureMode` as keyof RockBTSSpecimen, e.target.value || null)}
        InputProps={{ readOnly }} sx={inputSx} disabled={disabled}
      >
        <MenuItem value="">—</MenuItem>
        {BTS_FRACTURE_MODE_OPTIONS.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
      </TextField>
      <TextField
        select size="small" label="Plane Type"
        value={(specimen[`${prefix}PlaneType` as keyof RockBTSSpecimen] as string | null) ?? ''}
        onChange={(e) => onChange(`${prefix}PlaneType` as keyof RockBTSSpecimen, e.target.value || null)}
        InputProps={{ readOnly }} sx={inputSx} disabled={disabled}
      >
        <MenuItem value="">—</MenuItem>
        {BTS_PLANE_TYPE_OPTIONS.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
      </TextField>
      <NumericTextField
        size="small" label="vs load axis (a)"
        value={specimen[`${prefix}AngleADeg` as keyof RockBTSSpecimen] as number | null}
        onChange={num(`${prefix}AngleADeg` as keyof RockBTSSpecimen)}
        InputProps={{ endAdornment: unit('°'), readOnly }} sx={inputSx} disabled={disabled}
      />
      <NumericTextField
        size="small" label="vs core axis (b)"
        value={specimen[`${prefix}AngleBDeg` as keyof RockBTSSpecimen] as number | null}
        onChange={num(`${prefix}AngleBDeg` as keyof RockBTSSpecimen)}
        InputProps={{ endAdornment: unit('°'), readOnly }} sx={inputSx} disabled={disabled}
      />
    </Box>
  );

  return (
    <Paper variant="outlined" sx={paperSx}>
      <SectionHeader label={title} />

      {/* ── Loading information (flat platens hard-coded — REQ-05/D4) ── */}
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.75 }}>
        <SectionHeader label="Loading Information" color="text.secondary" />
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.66rem' }}>
          Platen type: Flat (ASTM D3967)
        </Typography>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25, mb: 0.5 }}>
        <NumericTextField
          size="small" label="Loading rate (kN/s) *"
          value={specimen.loadingRateKNs} onChange={num('loadingRateKNs')}
          InputProps={{ endAdornment: unit('kN/s'), readOnly }} sx={inputSx}
          error={preview.rateOutsideAstmRange === true}
          helperText={preview.rateOutsideAstmRange === true ? 'Outside the ASTM D3967 range (advisory)' : undefined}
        />
        <Box sx={{ display: 'grid', alignContent: 'center', rowGap: 0.25 }}>
          <DerivedValue
            label="Min Rate"
            value={fmt(preview.minRateKNs, 2)}
            suffix="kN/s"
            formula={formulaFor('minRateKNs')?.formula}
            note={formulaFor('minRateKNs')?.note}
          />
          <DerivedValue
            label="Max Rate"
            value={fmt(preview.maxRateKNs, 2)}
            suffix="kN/s"
            formula={formulaFor('maxRateKNs')?.formula}
            note={formulaFor('maxRateKNs')?.note}
          />
        </Box>
        <DerivedValue
          label="Loading Rate (MPa/s)"
          value={fmt(preview.loadingRateMPas, 2)}
          suffix="MPa/s"
          formula={formulaFor('loadingRateMPas')?.formula}
          note={formulaFor('loadingRateMPas')?.note}
        />
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25, alignItems: 'center', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <NumericTextField
            size="small" label="Maximum Load at Failure (kN) *"
            value={specimen.maxLoadKN} onChange={num('maxLoadKN')}
            InputProps={{ endAdornment: unit('kN'), readOnly }} sx={{ ...inputSx, flex: 1 }}
          />
          <FieldEvidenceButton fieldKey={`maxLoad${specimen.specimenNumber}`} />
        </Box>
        <DerivedValue
          label="Tensile Strength (indirect)"
          value={fmt(preview.tensileStrengthMPa, 1)}
          suffix="MPa"
          formula={formulaFor('tensileStrengthMPa')?.formula}
          note={formulaFor('tensileStrengthMPa')?.note}
        />
      </Box>

      {/* ── Failure type + deviation helper (REQ-09 — advisory, not reported) ── */}
      <Divider sx={{ mb: 1 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <SectionHeader label="Failure Type & Measurements" color="text.secondary" />
        {/* Green — fracture / failure type examples */}
        <ReferenceFigureButton
          title="Fracture / failure type examples"
          image={fractureTypesFigure}
        />
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr', gap: 1.25, mb: 0.75 }}>
        <TextField
          select size="small" label="Failure Type *"
          value={specimen.failureType ?? ''}
          onChange={(e) => onChange('failureType', e.target.value || null)}
          InputProps={{ readOnly }} sx={inputSx}
        >
          <MenuItem value="">—</MenuItem>
          {BTS_FAILURE_TYPE_OPTIONS.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
        </TextField>
        <NumericTextField
          size="small" label="X — Front"
          value={specimen.deviationXFrontMm} onChange={num('deviationXFrontMm')}
          InputProps={{ endAdornment: unit('mm'), readOnly }} sx={inputSx}
        />
        <NumericTextField
          size="small" label="X — Back"
          value={specimen.deviationXBackMm} onChange={num('deviationXBackMm')}
          InputProps={{ endAdornment: unit('mm'), readOnly }} sx={inputSx}
        />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
        <DerivedValue
          label="Average X"
          value={fmt(preview.deviationXAvgMm, 2)}
          suffix="mm"
          formula={formulaFor('deviationXAvgMm')?.formula}
          note={formulaFor('deviationXAvgMm')?.note}
        />
        <DerivedValue
          label="Deviation"
          value={fmt(preview.deviationRatio, 4)}
          formula={formulaFor('deviationRatio')?.formula}
          note={formulaFor('deviationRatio')?.note}
        />
        {preview.suggestedClassification && (
          <Chip
            size="small"
            label={`Suggests: ${preview.suggestedClassification}`}
            color={preview.suggestedClassification === 'Central' ? 'success' : 'warning'}
            sx={{ height: 18, fontSize: '0.65rem' }}
          />
        )}
        {/* Yellow — central / non-central deviation (X) measurement reference */}
        <ReferenceFigureButton
          title="Central / non-central deviation measurement (X)"
          image={deviationFigure}
        />
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.64rem', display: 'block' }}>
        {BTS_NOTE_1}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.64rem', display: 'block', mb: 1.5 }}>
        {BTS_NOTE_2} If deviation &gt; 0.1, fracture type is suggested Non-Central — operator judgment prevails;
        this helper is not printed on the report.
      </Typography>

      {/* ── Failure rows (REQ-08) ── */}
      <Divider sx={{ mb: 1 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <SectionHeader label="Failure (Primary)" color="text.secondary" />
        {/* Blue — failure description examples, with the failure-angle (a/b) figure (orange) as a secondary image */}
        <ReferenceFigureButton
          title="Failure description examples"
          image={failureExamplesFigure}
          secondaryImage={axisAnglesFigure}
          secondaryTitle="Failure angles — load axis (a) / core axis (b)"
        />
      </Box>
      {failureRow('primary', false)}
      <Box sx={{ mt: 1 }}>
        <SectionHeader
          label={hasSecondary ? 'Failure (Secondary — used by "& Layer Activation")' : 'Failure (Secondary — set "Failure Type" above to a "& Layer Activation" option to enable)'}
          color="text.secondary"
        />
        {failureRow('secondary', !hasSecondary)}
        {/* BUG-2026-0156 — the old hint ('select a "& Layer Activation" type') named no field, so
            operators hunted in the primary Fracture Mode dropdown. Spell out the exact field,
            its section, and the qualifying options (sourced from BTS_FAILURE_TYPES_WITH_SECONDARY
            so the list can never drift from the actual gate). */}
        {!hasSecondary && (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.64rem', display: 'block', mt: 0.5 }}>
            Locked until &quot;Failure Type&quot; (in Failure Type &amp; Measurements above) is one of:{' '}
            {[...BTS_FAILURE_TYPES_WITH_SECONDARY].map((t) => `"${t}"`).join(', ')}. The secondary row
            describes the layer-activation part of those combined failures.
          </Typography>
        )}
      </Box>

      {/* ── Auto description + override + remarks ── */}
      <Divider sx={{ my: 1 }} />
      <SectionHeader label="Failure Description" color="text.secondary" />
      <Typography variant="caption" sx={{ fontSize: '0.72rem', display: 'block', mb: 0.75, fontStyle: 'italic' }}>
        Auto-generated: {preview.failureDescription ?? '— complete the failure selections —'}
      </Typography>
      <Box sx={{ display: 'grid', rowGap: 1.25 }}>
        <TextField
          size="small" fullWidth multiline minRows={2}
          label="Override (leave empty to use the auto-generated sentence)"
          value={specimen.failureDescriptionOverride ?? ''}
          onChange={(e) => onChange('failureDescriptionOverride', e.target.value || null)}
          InputProps={{ readOnly }} sx={inputSx}
        />
        <TextField
          size="small" fullWidth multiline minRows={2}
          label="Remarks (internal — not printed on the report)"
          value={specimen.failureRemarks ?? ''}
          onChange={(e) => onChange('failureRemarks', e.target.value || null)}
          InputProps={{ readOnly }} sx={inputSx}
        />
      </Box>
    </Paper>
  );
};
